/* eslint-disable react/no-danger, react/prop-types */
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import {
  Typography,
  Tag,
  Grid,
  GridItem,
  CloudinaryPicture,
  Button,
  TypographyWrapper,
} from '@bwoty-web/ui-kit';
import Heading from './heading/Heading';
import Description from '../../common/description';
import Ratings from './ratings/Ratings';
import UspList from '../../common/uspList';
import { logGeoInformation } from '../../utils/log';
import {
  isArea,
  isCountry,
  isResort,
  isRegion,
  itemTypes,
} from '../../utils/geoTypeUtils';

import './geoInformation.scss';

const imageSizes = [
  {
    width: 1280,
    breakpoint: 976,
  },
  {
    width: 975,
    breakpoint: 769,
  },
  {
    width: 768,
    breakpoint: 416,
  },
  {
    width: 415,
    breakpoint: 376,
  },
  {
    width: 375,
  },
];

const getImageSizes = (columnSpan, aspectRatio, aspectRatioMobile) => {
  const columnRatio = (columnSpan || 12) / 12;

  return imageSizes.map((size) => {
    const isMobile = !size.breakpoint || size.breakpoint < 768;
    const width = isMobile ? size.width : size.width * columnRatio;
    const checkedAspectRatio = isMobile
      ? aspectRatioMobile || aspectRatio
      : aspectRatio;
    const height = width / checkedAspectRatio;

    return {
      ...size,
      width: Math.round(width),
      height: Math.round(height),
    };
  });
};

const GeoInformationView = ({ geoInformation, itemType }) => {
  const {
    name,
    hotelFinderUrl,
    descriptionParts,
    description,
    geographical,
    // numberOfHotels,
    labels,
    uspList,
    rating,
    isSuitableForTraining,
    images,
  } = geoInformation;

  const isGlobe = useIsGlobetrotter();
  const handleClickOnHotelLink = () => {
    logGeoInformation.clickOnHotelsButton(itemType);
  };
  const containerMicroDataItemType = () => {
    let microDataItemType;
    switch (itemType) {
      case itemTypes.resort:
      case itemTypes.area:
        microDataItemType = 'City';
        break;
      case itemTypes.country:
        microDataItemType = 'Country';
        break;
      case itemTypes.region:
        microDataItemType = 'Continent';
        break;
      default:
        microDataItemType = false;
        break;
    }
    return microDataItemType;
  };

  const containerMicroDataItemRef = () => {
    let microDataItemRef;
    switch (itemType) {
      case itemTypes.resort:
        microDataItemRef = 'resort-image';
        break;
      case itemTypes.area:
        microDataItemRef = 'area-image';
        break;
      case itemTypes.country:
        microDataItemRef = 'country-image';
        break;
      case itemTypes.region:
        microDataItemRef = 'region-image';
        break;
      default:
        microDataItemRef = false;
        break;
    }
    return microDataItemRef;
  };

  const structuredData = JSON.stringify({
    '@context': 'http://schema.org/',
    '@type': containerMicroDataItemType(),
    name,
    image: {
      '@id': containerMicroDataItemRef(),
    },
  });

  const structuredDataProduct = JSON.stringify({
    '@context': 'http://schema.org/',
    '@type': 'Product',
    name,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating.averageGuestRating,
      bestRating: '5',
      ratingCount: rating.numberOfVotes,
    },
  });

  const containerClasses = classNames({
    'gw-information-container': true,
    'gw-information-container--area': isArea(itemType),
    'gw-information-container--country': isCountry(itemType),
    'gw-information-container--region': isRegion(itemType),
  });

  const rightColumnClasses = classNames({
    'gw-information__right': true,
    'gw-information__right--area': isArea(itemType),
    'gw-information__right--country': isCountry(itemType),
    'gw-information__right--region': isRegion(itemType),
    'gw-information__right--area-no-subtitle': !geographical,
  });

  const heading = (
    <Heading
      destinationName={name}
      geographical={geographical}
      titlePrefix={labels.titlePrefix}
      hideSubtitle={isRegion(itemType)}
    />
  );

  const hotelLinkBtn = (
    <Button
      className="gw-information__right-items-link"
      href={hotelFinderUrl}
      onClick={hotelFinderUrl ? handleClickOnHotelLink : undefined}
      variant="primary"
    >
      {`${labels.hotelsLinkText}`}
    </Button>
  );

  if (isGlobe) {
    return (
      <div className="gw-info-globe">
        <div className="gw-info-globe-ingress">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: structuredData }}
          />
          <Typography className="gw-info-globe-ingress__eyebrow" variant="mini">
            {labels.aboutTabLink}
          </Typography>
          <Typography
            as="h1"
            itemProp="name"
            className="gw-info-globe-ingress__title"
            variant="headline-l"
          >
            {name?.toUpperCase()}
          </Typography>
          <Typography className="gw-info-globe-ingress__preamble">
            {descriptionParts?.preamble?.content}
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div className="gw-info-globe-ingress__separator" />
          </div>
        </div>

        {descriptionParts.introduction && (
          <Grid disableAutoSizing className="gw-info-globe-introduction">
            <GridItem columnSpan={5}>
              <div className="gw-info-globe-image-wrapper">
                <CloudinaryPicture
                  className="gw-info-globe-image"
                  retina
                  sizes={getImageSizes(5, 3 / 2)}
                  img={images[0]}
                />
              </div>
            </GridItem>
            <GridItem className="gw-info-globe-empty-col" columnSpan={1} />
            <GridItem columnSpan={5}>
              <div className="gw-info-globe-text-container">
                <Typography variant="headline-m">
                  {descriptionParts.introduction.heading}
                </Typography>
                <TypographyWrapper
                  dangerouslySetInnerHTML={{
                    __html: descriptionParts.introduction.content,
                  }}
                />
              </div>
            </GridItem>
            <GridItem className="gw-info-globe-empty-col" columnSpan={1} />
          </Grid>
        )}
        {descriptionParts.product && (
          <Grid className="gw-info-globe-product" disableAutoSizing>
            <GridItem className="gw-info-globe-empty-col" columnSpan={1} />
            <GridItem columnSpan={6}>
              <div className="gw-info-globe-text-container">
                <Typography variant="headline-m">
                  {descriptionParts.product.heading}
                </Typography>
                <TypographyWrapper
                  dangerouslySetInnerHTML={{
                    __html: descriptionParts.product.content,
                  }}
                />
              </div>
            </GridItem>
            <GridItem className="gw-info-globe-empty-col" columnSpan={1} />
            <GridItem className="gw-info-globe-product__image" columnSpan={4}>
              <div className="gw-info-globe-image-wrapper">
                <CloudinaryPicture
                  className="gw-info-globe-image"
                  retina
                  sizes={getImageSizes(4, 4 / 5, 4 / 5)}
                  img={images[1]}
                />
              </div>
            </GridItem>
          </Grid>
        )}
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: structuredData }}
      />
      {rating && rating.averageGuestRating && rating.numberOfVotes > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: structuredDataProduct }}
        />
      )}
      <div className="gw-information uikit-box">
        <div className="gw-information__left">
          {isSuitableForTraining && (
            <Tag className="gw-information__tag" small>
              {labels.isSuitableForTrainingText}
            </Tag>
          )}
          {heading}
          <Description
            paragraphs={description}
            showMoreText={`${labels?.showMoreText} ${geoInformation.name}`}
            hideText={labels?.hideText}
            geoType={itemType}
            mobileHeading={heading}
          />
          {isResort(itemType) && (
            <Ratings
              heading={rating.heading}
              labels={geoInformation.labels}
              averageGuestRating={rating.averageGuestRating}
              numberOfVotes={rating.numberOfVotes}
              categoryRatings={rating.categoryRatings}
              destinationName={geoInformation.name}
            />
          )}
          <div className="gw-information__buttons">
            {hotelLinkBtn}
            {!isResort(itemType) && (
              <Button
                className="gw-information__right-items-link"
                href={labels.resortsLink}
              >
                {labels.resortsLinkText}
              </Button>
            )}
          </div>
        </div>
        {uspList?.length > 1 && (
          <div className={rightColumnClasses}>
            <div className="gw-information-usp-list-box">
              <div className="gw-information-usp-list-box__title">
                {labels.uspHeader}
              </div>
              <UspList usps={uspList.map((item) => item.content)} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

GeoInformationView.propTypes = {
  geoInformation: PropTypes.shape({
    name: PropTypes.string.isRequired,
    geographical: PropTypes.shape({
      region: PropTypes.shape({}),
      country: PropTypes.shape({}),
      area: PropTypes.shape({}),
    }).isRequired,
    uspList: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string.isRequired,
      }).isRequired,
    ),
    rating: PropTypes.shape({
      heading: PropTypes.string,
      averageGuestRating: PropTypes.number,
      numberOfVotes: PropTypes.string,
      categoryRatings: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    numberOfHotels: PropTypes.number.isRequired,
    isSuitableForTraining: PropTypes.bool,
    isDynamicPackage: PropTypes.bool,
    description: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    labels: PropTypes.shape({
      titlePrefix: PropTypes.string,
      uspHeader: PropTypes.string,
      hotelsLinkText: PropTypes.string,
      resortsLinkText: PropTypes.string,
      showMoreText: PropTypes.string,
      hideText: PropTypes.string,
      isSuitableForTrainingText: PropTypes.string,
      aboutTabLink: PropTypes.string,
    }).isRequired,
  }).isRequired,
  itemType: PropTypes.string.isRequired,
};

export default GeoInformationView;
